import {Authenticator} from '@aws-amplify/ui-react';
import {MantineProvider} from '@mantine/core';
import {Notifications} from '@mantine/notifications';

import {ErrorBoundary} from 'react-error-boundary';
import {Provider} from 'react-redux';
import {PersistGate} from 'redux-persist/integration/react';
import {
  configureAmplify,
  readSsoClientId,
} from 'src/app/security/authentication/configureAmplify';
import Routing from 'src/pages/Routing';
import 'src/pages/routes';
import 'src/shared/utils/AxiosInterceptors';
import {theme} from 'src/theme';
import {persistor, store} from './redux/store';

import {DatesProvider} from '@mantine/dates';

// Must run before anything reads the OAuth callback URL: the callback has to
// use the same client id as the redirect.
configureAmplify(readSsoClientId() ?? undefined);

function GlobalErrorFallback({error}: any) {
  return (
    // Plain elements, not Mantine: this fallback renders outside MantineProvider.
    <div
      style={{
        margin: 48,
        display: 'flex',
        alignContent: 'center',
        flexDirection: 'column',
      }}>
      <div style={{maxWidth: '1000px'}}>
        <h1>Proact EHS</h1>
        <h2>Sorry, a fatal error occured.</h2>
        <p>
          Hi! Unfortunately, a fatal error occured while loading the
          application. This is likely an issue with the system and not due to
          your actions. If you see this error page, please try to refresh the
          page. If the error repeats itself, please copy the contents of the
          grey box below and share it with our support team. Thank you for your
          patience.
        </p>
      </div>
      <div
        style={{
          marginTop: 24,
          padding: 24,
          background: 'darkgray',
          borderRadius: '3px',
        }}>
        <div>
          <b>Error Message:</b> {error.message}
        </div>
        <div>
          <b>Error Details:</b>
        </div>
        <div>{JSON.stringify(error)}</div>
      </div>
    </div>
  );
}

function isLocalStorageAvailable() {
  const test = 'test';
  try {
    localStorage.setItem(test, test);
    const result = localStorage.getItem(test);
    localStorage.removeItem(test);
    return result === test;
  } catch {
    return false;
  }
}

function App() {
  if (isLocalStorageAvailable() == false) {
    return (
      <GlobalErrorFallback
        error={{
          message:
            'LocalStorage is disabled in browser. LocalStorage is a required feature to use this application.',
        }}
      />
    );
  }

  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <MantineProvider theme={theme}>
        <Notifications />
        <DatesProvider settings={{}}>
          <Authenticator.Provider>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <Routing />
              </PersistGate>
            </Provider>
          </Authenticator.Provider>
        </DatesProvider>
      </MantineProvider>
    </ErrorBoundary>
  );
}

export default App;
